import { DateTime } from 'luxon'
import * as yup from 'yup'
import {
  buildDateTime,
  buildDateTimeForNative,
  isInvalidDate,
} from 'lib/CustomLuxonUtils'

const hasValidDateTimeValue = value => value != null && !isInvalidDate(value)

const isJsDate = value =>
  value instanceof Date && !Number.isNaN(value.getTime())

const sessionStartDateTime = session => {
  if (!session?.startDate || !session?.startTime) return null

  // Lightning uses Yup.date() + HH:mm strings. Create/edit yup.date()
  // coerces Luxon to JS Date. String times must take the native path first
  // so mixed Date + "14:30" still validates open-before-start/close.
  if (
    typeof session.startDate === 'string' ||
    typeof session.startTime === 'string'
  ) {
    return buildDateTimeForNative(session.startDate, session.startTime)
  }

  if (
    DateTime.isDateTime(session.startDate) ||
    DateTime.isDateTime(session.startTime) ||
    isJsDate(session.startDate) ||
    isJsDate(session.startTime)
  ) {
    return buildDateTime(session.startDate, session.startTime)
  }

  return null
}

const hoursBeforeClose = hoursBefore => {
  if (hoursBefore === 'none' || hoursBefore == null || hoursBefore === '') {
    return null
  }
  const hours = Number(hoursBefore)
  return Number.isFinite(hours) ? hours : null
}

export const getRegistrationOpenDateTime = (formValues, timezone) => {
  if (formValues.isExternalRegistration) return null

  const { registrationOpenDate, registrationOpenTime } = formValues
  if (
    !hasValidDateTimeValue(registrationOpenDate) ||
    !hasValidDateTimeValue(registrationOpenTime)
  ) {
    return null
  }

  return buildDateTime(registrationOpenDate, registrationOpenTime, timezone)
}

export const registrationOpenDateTimeSchema = {
  registrationOpenDate: yup.mixed().nullable(),
  registrationOpenTime: yup
    .mixed()
    .nullable()
    .test(
      'open-date-and-time',
      'Select a date and time, or leave both blank',
      function (time) {
        const date = this.parent.registrationOpenDate
        return hasValidDateTimeValue(date) === hasValidDateTimeValue(time)
      },
    )
    .test(
      'open-before-event-start',
      'Registration must open before the event starts',
      function (time) {
        const date = this.parent.registrationOpenDate
        if (!hasValidDateTimeValue(date) || !hasValidDateTimeValue(time)) {
          return true
        }
        const session = this.parent.sessions?.[0]
        const start = sessionStartDateTime(session)
        if (!start?.isValid) return true
        const open = buildDateTime(date, time)
        if (!open?.isValid) return true
        return open < start
      },
    )
    .test(
      'open-before-close',
      'Registration must open before it closes',
      function (time) {
        const date = this.parent.registrationOpenDate
        if (!hasValidDateTimeValue(date) || !hasValidDateTimeValue(time)) {
          return true
        }
        const hours = hoursBeforeClose(
          this.parent.hoursBeforeRegistrationCloses,
        )
        if (hours == null) return true
        const session = this.parent.sessions?.[0]
        const start = sessionStartDateTime(session)
        if (!start?.isValid) return true
        const close = start.minus({ hours })
        const open = buildDateTime(date, time)
        if (!open?.isValid) return true
        return open < close
      },
    ),
}
